/**
 * @namespace ranj
 * @param {Object} ranj
 */

import IClock from "./IClock";

/**
 * The GameClock class implements the IClock interface with a game controlled clock. This
 * clock only counts time in javascript and does not use the real clock as a reference. You have to
 * start this clock manually and can do things like pauze etc. This clock is very usefull in measuring
 * in-game time. 
 * 
 * <p>When you have some save mechanism in the game you should pauze the clock on the save action and store
 * the currentTime with your save data. When you load the game you should pass the time to the start function
 * so the clock starts running from the time you saved.</p>
 *
 * <p>
 * NOTE: all time values are based on milliseconds
 * </p>
 * @constructor
 **/
export default class GameClock extends IClock {
    constructor() {
        super();
        this.reset();
    }

    /**
     * Get the current time on the clock 
     * @returns {number} The current time as an int
     */
    getCurrentTime() {

        this._updateTime();
        return (this._superCurrentTime() - this._lastNormalTime) + this._currentTimeOffset;
    }

    /**
     * Reset the GameClock
     * @returns {undefined}
     */
    reset() {
        this._lastTime = 0;
        this._started = false;
        this._paused = false;
        this._speed = 1;
        this._currentTimeOffset = 0;
        this._lastNormalTime = 0;
    }

    /**
     * Start the clock, this function acts different in different cases:
     * <ul>
     * <li>If the clock is not yet started or pauzed the clock is started using the param startTime as starting value</li>
     * <li>If the clock is pauzed it is resumed, the value of startTime is ignored</li>
     * <li>If the clock is started and not pauzed nothing is done</li>
     * </ul>
     * @param {number} startTime
     * @returns {undefined}
     */
    start(startTime) {
        if (this._paused) {
            this._paused = false;
            this._started = true;
            this._lastFlashTime = Date.now();
        } else if (!this._started) {
            startTime = (typeof startTime === "undefined") ? 0 : startTime;

            this._lastTime = startTime;
            this._lastFlashTime = Date.now();
            this._started = true;
        }
    }

    /**
     * Stops the clock and resets the time 
     * @returns {undefined}
     */
    stop() {
        this.reset();
    }

    /**
     * Pauzes the clock, by calling start again you can have clock run again 	
     * @returns {undefined}
     */
    pause() {
        if (!this._paused) {
            this._lastTime = this._superCurrentTime();
            this._started = false;
            this._paused = true;
        }
    }

    /**
     * Returns whether the clock is paused
     * @returns {boolean}
     */
    isPaused() {
        return this._paused;
    }

    /**
     * Returns whether the clock is started 
     * @returns {boolean}
     */
    isStarted() {
        return this._started;
    }

    setSpeed(val) {
        if (this._speed === val)
            return;

        this._updateTime();

        //Set speed
        this._speed = val;
    }

    _updateTime() {
        this._currentTimeOffset += (this._superCurrentTime() - this._lastNormalTime) * this._speed;
        this._lastNormalTime = this._superCurrentTime();
    }

    _superCurrentTime() {
        if (this._started) {
            const curTime = Date.now();
            return this._lastTime + (curTime - this._lastFlashTime);
        } else {
            return this._lastTime;
        }
    }

    getSpeed(val) {
        return this._speed;
    }
}




